/**
 * The deal's ServiceTitan customer — find one ServiceTitan already has, or add one.
 *
 * Opened from the sidebar's "ST customer" row on a deal with no customer in ServiceTitan.
 * The dialog first lists the brand family's existing ServiceTitan customers this deal's
 * customer could be: the same email, or the same street, unit and ZIP. Missing phone,
 * email or address are only flagged — nothing is written to ServiceTitan's record.
 *
 * ## Link
 *
 * "This is the same person" (on by default for a same-email match, off for an address
 * match) merges the two HickoryOS customer records and tags the ServiceTitan customer
 * with HickoryOS's ids. Unticked, the deal alone is linked. The link id is minted once per
 * open, so a retry after a lost response is answered with the first write.
 *
 * ## Create
 *
 * The same customer create an Assign → ServiceTitan enqueues. Any match blocks a plain
 * create. When every match is address-only the rep may "Create new anyway"; a same-email
 * match may only be linked. A deal with no address on file asks for one, and the list
 * follows the address as it is typed.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { notifyStHandoff, type StHandoff } from "@/components/records/st-handoff";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ResponsiveOverlay } from "@/components/ui/responsive-overlay";
import {
  useCreateStCustomer,
  useLinkStCustomer,
  useStCustomerCandidates,
  fetchStCustomerLinkStatus,
  followLate,
  type StCustomerCandidate,
} from "@/hooks/use-deal-board";
import { useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

import { ServiceAddressFields } from "./service-address-fields";
import {
  EMPTY_SERVICE_ADDRESS,
  serviceAddressState,
  toServiceAddress,
  type ServiceAddressDraft,
  type ServiceAddressWire,
} from "./service-address";

export interface StCustomerTarget {
  dealId: string;
  dealLabel: string;
  /** The deal has a service address on file; without one, a create asks for it. */
  hasAddress: boolean;
}

/** How long the typed address rests before the list re-checks it. */
const ADDRESS_DEBOUNCE_MS = 400;

const MATCH_LABEL: Record<StCustomerCandidate["match"], string> = {
  // A sister brand's record of this same customer — shown when two of them disagree.
  same_person: "Same person",
  same_email: "Same email",
  same_address: "Same address",
};

function missingLine(missing: StCustomerCandidate["missing_in_st"]): string | null {
  if (missing.length === 0) return null;
  return `ServiceTitan doesn't have this lead's ${missing.join(", ")}`;
}

export function StCustomerDialog({
  target,
  onOpenChange,
}: {
  target: StCustomerTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  // The idempotency key: one per open (the opener mounts the dialog per open).
  const [linkId] = useState(() => crypto.randomUUID());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [samePerson, setSamePerson] = useState(false);
  const [draft, setDraft] = useState<ServiceAddressDraft>(EMPTY_SERVICE_ADDRESS);
  // A deal whose address the server could not use (422) asks for one too.
  const [addressAsked, setAddressAsked] = useState(false);
  const [queriedAddress, setQueriedAddress] = useState<ServiceAddressWire | null>(null);

  const dealId = target?.dealId ?? null;
  const needsAddress = target !== null && (!target.hasAddress || addressAsked);
  const typed = needsAddress && serviceAddressState(draft) === "complete" ? toServiceAddress(draft) : null;
  // A string key, so the debounce timer restarts only when the address itself changes.
  const typedKey = typed ? JSON.stringify(typed) : "";
  useEffect(() => {
    const timer = setTimeout(
      () => setQueriedAddress(typedKey ? (JSON.parse(typedKey) as ServiceAddressWire) : null),
      ADDRESS_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [typedKey]);

  const candidatesQuery = useStCustomerCandidates(dealId, queriedAddress);
  const create = useCreateStCustomer();
  const link = useLinkStCustomer();
  const queryClient = useQueryClient();

  if (!target) return null;

  const data = candidatesQuery.data;
  const candidates = data?.candidates ?? [];
  const selected = candidates.find((c) => c.st_customer_id === selectedId) ?? null;
  const alreadyHas = data?.current_st_customer_id ?? null;
  const submitting = create.isPending || link.isPending;
  // The list has caught up with what the rep typed: a create is checked against it.
  const settled =
    data !== undefined &&
    !candidatesQuery.isFetching &&
    (queriedAddress ? JSON.stringify(queriedAddress) : "") === typedKey;
  const allowsCreate = candidates.every((c) => c.allows_create);
  const canOfferCreate = data !== undefined && alreadyHas === null && data.create_enabled && allowsCreate;
  const addressReady = !needsAddress || typed !== null;
  const createReady = canOfferCreate && settled && addressReady && !submitting;
  // A choice is linked against the list it was made from: not while that list is catching up.
  const linkReady = selected !== null && settled && !submitting;

  function choose(candidate: StCustomerCandidate) {
    setSelectedId(candidate.st_customer_id);
    // Ticked only for a strong email match — the one ServiceTitan customer that carries
    // the lead's own email. A property manager's shared email or an address is unticked.
    setSamePerson(!candidate.allows_create);
  }

  function submitLink() {
    if (!target || !selected || submitting) return;
    link.mutate(
      {
        dealId: target.dealId,
        linkId,
        stCustomerId: selected.st_customer_id,
        samePerson,
        address: needsAddress ? typed : null,
      },
      {
        onSuccess: (result) => {
          toast.success(`Linked to ServiceTitan customer ${result.st_customer_id}`, {
            description: result.same_person
              ? "HickoryOS is merging the two customer records and tagging the ServiceTitan customer."
              : "Linked for this deal only.",
          });
          // Canonical resolution decides the merge after the dialog closes: follow it, and say
          // so when it refuses (a quarantine or a "not the same person" hold), so the rep is
          // never told two records merged that did not.
          if (result.same_person) {
            const dealId = target.dealId;
            followLate(
              () => fetchStCustomerLinkStatus(queryClient, dealId, result.link_id),
              (status) => {
                if (status.state === "merged") toast.success("The two customer records are merged.");
                if (status.state === "refused") {
                  toast.warning("The two customer records weren't merged", {
                    description: `The deal stays linked to ServiceTitan customer ${result.st_customer_id}; the identity review keeps the records apart.${status.error ? ` (${status.error})` : ""}`,
                  });
                }
              },
            );
          }
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error("Couldn't link it", {
            description: apiErrorMessage(error, "Try again in a moment."),
          });
          if (error.status === 404 || error.status === 409) void candidatesQuery.refetch();
        },
      },
    );
  }

  function submitCreate() {
    if (!target || !createReady) return;
    create.mutate(
      {
        dealId: target.dealId,
        address: needsAddress ? typed : null,
        createAnyway: candidates.length > 0,
      },
      {
        onSuccess: (result) => {
          notifyStHandoff({
            action: result.action,
            owner_resolved: false,
            owner_name: null,
            reason: result.reason as StHandoff["reason"],
            st_customer_id: result.st_customer_id,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          if (error.status === 422) setAddressAsked(true);
          // A customer landed in ServiceTitan since the list was read: show it.
          if (error.status === 409) void candidatesQuery.refetch();
          toast.error("Couldn't add the customer", {
            description: apiErrorMessage(error, "Try again in a moment."),
          });
        },
      },
    );
  }

  return (
    <ResponsiveOverlay
      kind="dialog"
      open
      onOpenChange={(open) => !submitting && onOpenChange(open)}
      title="ServiceTitan Customer"
      description={target.dealLabel}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          {canOfferCreate ? (
            <Button
              variant={candidates.length > 0 ? "outline" : "default"}
              onClick={submitCreate}
              disabled={!createReady}
              aria-busy={create.isPending}
            >
              {create.isPending ? "Adding…" : candidates.length > 0 ? "Create New Anyway" : "Create in ServiceTitan"}
            </Button>
          ) : null}
          {candidates.length > 0 && alreadyHas === null ? (
            <Button onClick={submitLink} disabled={!linkReady} aria-busy={link.isPending}>
              {link.isPending ? "Linking…" : "Link"}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        {candidatesQuery.isPending ? (
          <p className="text-sm text-muted-foreground" role="status">
            Checking ServiceTitan for this customer…
          </p>
        ) : candidatesQuery.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {apiErrorMessage(candidatesQuery.error, "Couldn't check ServiceTitan. Try again in a moment.")}
          </p>
        ) : alreadyHas ? (
          <p className="text-sm text-muted-foreground">
            This deal's customer is already in ServiceTitan as customer {alreadyHas}.
          </p>
        ) : (
          <>
            {candidates.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm">ServiceTitan may already have this customer:</p>
                <ul className="space-y-2" aria-label="Matching ServiceTitan customers">
                  {candidates.map((c) => (
                    <CandidateCard
                      key={c.st_customer_id}
                      candidate={c}
                      selected={c.st_customer_id === selectedId}
                      samePerson={samePerson}
                      onSamePersonChange={setSamePerson}
                      onChoose={() => choose(c)}
                      disabled={submitting}
                    />
                  ))}
                </ul>
              </div>
            ) : null}

            {candidates.length > 0 && data?.create_enabled && !allowsCreate ? (
              <p className="text-sm text-muted-foreground">
                ServiceTitan already has this person, so a new customer can't be created. Link theirs instead.
              </p>
            ) : null}

            {candidates.length === 0 && data && !data.create_enabled ? (
              <p className="text-sm text-muted-foreground">
                This brand doesn't create ServiceTitan customers from HickoryOS.
              </p>
            ) : null}

            {candidates.length === 0 && canOfferCreate ? (
              <p className="text-sm text-muted-foreground">
                ServiceTitan has no customer with this email or at this address.
              </p>
            ) : null}

            {/* A deal with no address can still be matched by one — to link, even on a brand that
                doesn't create customers from HickoryOS. */}
            {needsAddress && data !== undefined && alreadyHas === null ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Service address</Label>
                <p className="text-xs text-muted-foreground">
                  {data.create_enabled
                    ? "ServiceTitan needs an address to add the customer. The list above checks it as you type."
                    : "Type the service address to look for a ServiceTitan customer there."}
                </p>
                <ServiceAddressFields
                  value={draft}
                  onChange={setDraft}
                  disabled={submitting}
                  idPrefix="st-customer-address"
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </ResponsiveOverlay>
  );
}

function CandidateCard({
  candidate,
  selected,
  samePerson,
  onSamePersonChange,
  onChoose,
  disabled,
}: {
  candidate: StCustomerCandidate;
  selected: boolean;
  samePerson: boolean;
  onSamePersonChange: (next: boolean) => void;
  onChoose: () => void;
  disabled: boolean;
}) {
  const flag = missingLine(candidate.missing_in_st);
  const checkboxId = `st-customer-same-${candidate.st_customer_id}`;
  return (
    <li
      className={cn("space-y-2 rounded-(--radius) border p-3 text-sm", selected && "border-primary bg-muted/50")}
      aria-label={candidate.name ?? `ServiceTitan customer ${candidate.st_customer_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium break-words">{candidate.name ?? `Customer ${candidate.st_customer_id}`}</span>
            <Badge variant="secondary">{MATCH_LABEL[candidate.match]}</Badge>
          </div>
          {candidate.address ? <p className="text-xs break-words">{candidate.address}</p> : null}
          {candidate.phones.length > 0 ? (
            <p className="text-xs text-muted-foreground">{candidate.phones.join(" · ")}</p>
          ) : null}
          {candidate.emails.length > 0 ? (
            <p className="text-xs break-all text-muted-foreground">{candidate.emails.join(" · ")}</p>
          ) : null}
          {flag ? <p className="text-xs text-muted-foreground italic">{flag}</p> : null}
        </div>
        <Button
          type="button"
          variant={selected ? "selected" : "outline"}
          size="sm"
          className="shrink-0 max-md:h-11"
          onClick={onChoose}
          disabled={disabled}
          aria-pressed={selected}
        >
          {selected ? "Chosen" : "Choose"}
        </Button>
      </div>
      {selected ? (
        <div className="flex items-start gap-2 border-t pt-2">
          <Checkbox
            id={checkboxId}
            checked={samePerson}
            onCheckedChange={(checked) => onSamePersonChange(checked === true)}
            disabled={disabled}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor={checkboxId} className="font-medium">
              This is the same person
            </Label>
            <p className="text-xs text-muted-foreground">
              {samePerson
                ? "Merges the two HickoryOS customer records and tags the ServiceTitan customer."
                : "Links this deal only. The two customer records stay separate."}
            </p>
          </div>
        </div>
      ) : null}
    </li>
  );
}
